import { useState, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { Upload, CheckCircle, AlertCircle, FileText, X } from 'lucide-react'

interface FormData {
  fullName: string
  email: string
  phone: string
  position: string
  message: string
  privacyConsent: boolean
}

interface FormErrors {
  fullName?: string
  email?: string
  phone?: string
  position?: string
  message?: string
  privacyConsent?: string
  cv?: string
}

export function JobApplicationForm() {
  const { t } = useTranslation('common')
  const fileInputRef = useRef<HTMLInputElement>(null)

  const [formData, setFormData] = useState<FormData>({
    fullName: '',
    email: '',
    phone: '',
    position: '',
    message: '',
    privacyConsent: false,
  })

  const [errors, setErrors] = useState<FormErrors>({})
  const [cvFile, setCvFile] = useState<File | null>(null)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle')
  const [csrfToken] = useState(() => {
    // Generate a simple CSRF token (in production, this should come from the server)
    return btoa(Math.random().toString(36).substring(2) + Date.now().toString(36))
  })

  const positions = [
    { value: '', label: t('careers.form.positionPlaceholder') },
    { value: 'production', label: t('careers.form.positions.production') },
    { value: 'quality', label: t('careers.form.positions.quality') },
    { value: 'logistics', label: t('careers.form.positions.logistics') },
    { value: 'sales', label: t('careers.form.positions.sales') },
    { value: 'marketing', label: t('careers.form.positions.marketing') },
    { value: 'admin', label: t('careers.form.positions.admin') },
    { value: 'other', label: t('careers.form.positions.other') },
  ]

  const validateForm = (): boolean => {
    const newErrors: FormErrors = {}

    if (!formData.fullName.trim() || formData.fullName.length < 2) {
      newErrors.fullName = t('careers.form.errors.fullName')
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    if (!formData.email.trim() || !emailRegex.test(formData.email)) {
      newErrors.email = t('careers.form.errors.email')
    }

    const phoneRegex = /^[\d\s\+\-\(\)]{8,20}$/
    if (!formData.phone.trim() || !phoneRegex.test(formData.phone)) {
      newErrors.phone = t('careers.form.errors.phone')
    }

    if (!formData.position) {
      newErrors.position = t('careers.form.errors.position')
    }

    if (!formData.message.trim() || formData.message.length < 20) {
      newErrors.message = t('careers.form.errors.message')
    }

    if (!cvFile) {
      newErrors.cv = t('careers.form.errors.cv')
    }

    if (!formData.privacyConsent) {
      newErrors.privacyConsent = t('careers.form.errors.privacy')
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (file) {
      const allowedTypes = ['application/pdf', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document']
      const maxSize = 5 * 1024 * 1024 // 5MB

      if (!allowedTypes.includes(file.type)) {
        setErrors(prev => ({ ...prev, cv: t('careers.form.errors.cvType') }))
        return
      }

      if (file.size > maxSize) {
        setErrors(prev => ({ ...prev, cv: t('careers.form.errors.cvSize') }))
        return
      }

      setCvFile(file)
      setErrors(prev => ({ ...prev, cv: undefined }))
    }
  }

  const removeFile = () => {
    setCvFile(null)
    if (fileInputRef.current) {
      fileInputRef.current.value = ''
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!validateForm()) {
      return
    }

    setIsSubmitting(true)
    setSubmitStatus('idle')

    // Simulate API call
    await new Promise(resolve => setTimeout(resolve, 1500))

    // In a real implementation, you would send the form data to your backend
    // including the CSRF token for protection
    console.log('Form submitted with CSRF token:', csrfToken)
    console.log('Form data:', formData)
    console.log('CV file:', cvFile?.name)

    setIsSubmitting(false)
    setSubmitStatus('success')

    // Reset form after success
    setFormData({
      fullName: '',
      email: '',
      phone: '',
      position: '',
      message: '',
      privacyConsent: false,
    })
    setCvFile(null)
    if (fileInputRef.current) {
      fileInputRef.current.value = ''
    }
  }

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target
    const checked = (e.target as HTMLInputElement).checked

    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }))

    // Clear error when user starts typing
    if (errors[name as keyof FormErrors]) {
      setErrors(prev => ({ ...prev, [name]: undefined }))
    }
  }

  if (submitStatus === 'success') {
    return (
      <section className="section-padding bg-[#FDF8F3] relative" aria-labelledby="form-title">
        <div className="container-fiorucci">
          <div className="max-w-2xl mx-auto">
            <div className="bg-white rounded-2xl shadow-xl p-8 md:p-12 text-center">
              <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <CheckCircle className="w-10 h-10 text-green-600" />
              </div>
              <h2 id="form-title" className="text-2xl md:text-3xl font-serif font-bold text-[#4E2118] mb-4">
                {t('careers.form.successTitle')}
              </h2>
              <p className="text-[#4E2118]/70 mb-8">
                {t('careers.form.successMessage')}
              </p>
              <button
                onClick={() => setSubmitStatus('idle')}
                className="btn-primary"
              >
                {t('careers.form.submitAnother')}
              </button>
            </div>
          </div>
        </div>
      </section>
    )
  }

  return (
    <section className="section-padding bg-[#FDF8F3] relative" aria-labelledby="form-title">
      <div className="container-fiorucci">
        <div className="max-w-3xl mx-auto">
          {/* Section Header */}
          <div className="text-center mb-10">
            <span className="inline-block px-4 py-2 bg-[#DF631E]/10 text-[#DF631E] text-sm font-semibold rounded-full mb-4">
              {t('careers.form.badge')}
            </span>
            <h2 id="form-title" className="heading-section text-[#4E2118] mb-4">
              {t('careers.form.title')}
            </h2>
            <p className="text-[#4E2118]/70">
              {t('careers.form.subtitle')}
            </p>
          </div>

          {/* Form */}
          <form
            onSubmit={handleSubmit}
            className="bg-white rounded-2xl shadow-xl p-6 md:p-10"
            noValidate
          >
            {/* CSRF Token (hidden) */}
            <input type="hidden" name="csrfToken" value={csrfToken} />

            {submitStatus === 'error' && (
              <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg flex items-start gap-3">
                <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                <p className="text-red-700 text-sm">{t('careers.form.errorMessage')}</p>
              </div>
            )}

            <div className="space-y-6">
              {/* Full Name */}
              <div>
                <label htmlFor="fullName" className="block text-sm font-medium text-[#4E2118] mb-2">
                  {t('careers.form.fullName')} <span className="text-[#DF631E]">*</span>
                </label>
                <input
                  type="text"
                  id="fullName"
                  name="fullName"
                  value={formData.fullName}
                  onChange={handleInputChange}
                  className={`w-full px-4 py-3 rounded-lg border ${
                    errors.fullName ? 'border-red-300 focus:border-red-500' : 'border-[#F3E4D6] focus:border-[#DF631E]'
                  } focus:outline-none focus:ring-2 focus:ring-[#DF631E]/20 transition-colors`}
                  placeholder={t('careers.form.fullNamePlaceholder')}
                  aria-invalid={errors.fullName ? 'true' : 'false'}
                  aria-describedby={errors.fullName ? 'fullName-error' : undefined}
                />
                {errors.fullName && (
                  <p id="fullName-error" className="mt-1 text-sm text-red-600">{errors.fullName}</p>
                )}
              </div>

              {/* Email & Phone */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-[#4E2118] mb-2">
                    {t('careers.form.email')} <span className="text-[#DF631E]">*</span>
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    className={`w-full px-4 py-3 rounded-lg border ${
                      errors.email ? 'border-red-300 focus:border-red-500' : 'border-[#F3E4D6] focus:border-[#DF631E]'
                    } focus:outline-none focus:ring-2 focus:ring-[#DF631E]/20 transition-colors`}
                    placeholder={t('careers.form.emailPlaceholder')}
                    aria-invalid={errors.email ? 'true' : 'false'}
                    aria-describedby={errors.email ? 'email-error' : undefined}
                  />
                  {errors.email && (
                    <p id="email-error" className="mt-1 text-sm text-red-600">{errors.email}</p>
                  )}
                </div>

                <div>
                  <label htmlFor="phone" className="block text-sm font-medium text-[#4E2118] mb-2">
                    {t('careers.form.phone')} <span className="text-[#DF631E]">*</span>
                  </label>
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    value={formData.phone}
                    onChange={handleInputChange}
                    className={`w-full px-4 py-3 rounded-lg border ${
                      errors.phone ? 'border-red-300 focus:border-red-500' : 'border-[#F3E4D6] focus:border-[#DF631E]'
                    } focus:outline-none focus:ring-2 focus:ring-[#DF631E]/20 transition-colors`}
                    placeholder={t('careers.form.phonePlaceholder')}
                    aria-invalid={errors.phone ? 'true' : 'false'}
                    aria-describedby={errors.phone ? 'phone-error' : undefined}
                  />
                  {errors.phone && (
                    <p id="phone-error" className="mt-1 text-sm text-red-600">{errors.phone}</p>
                  )}
                </div>
              </div>

              {/* Position */}
              <div>
                <label htmlFor="position" className="block text-sm font-medium text-[#4E2118] mb-2">
                  {t('careers.form.position')} <span className="text-[#DF631E]">*</span>
                </label>
                <select
                  id="position"
                  name="position"
                  value={formData.position}
                  onChange={handleInputChange}
                  className={`w-full px-4 py-3 rounded-lg border ${
                    errors.position ? 'border-red-300 focus:border-red-500' : 'border-[#F3E4D6] focus:border-[#DF631E]'
                  } focus:outline-none focus:ring-2 focus:ring-[#DF631E]/20 transition-colors bg-white`}
                  aria-invalid={errors.position ? 'true' : 'false'}
                  aria-describedby={errors.position ? 'position-error' : undefined}
                >
                  {positions.map(pos => (
                    <option key={pos.value} value={pos.value}>{pos.label}</option>
                  ))}
                </select>
                {errors.position && (
                  <p id="position-error" className="mt-1 text-sm text-red-600">{errors.position}</p>
                )}
              </div>

              {/* CV Upload */}
              <div>
                <label className="block text-sm font-medium text-[#4E2118] mb-2">
                  {t('careers.form.cv')} <span className="text-[#DF631E]">*</span>
                </label>
                <div className={`border-2 border-dashed rounded-lg p-6 text-center transition-colors ${
                  errors.cv ? 'border-red-300 bg-red-50' : 'border-[#F3E4D6] hover:border-[#DF631E]/50'
                }`}>
                  {!cvFile ? (
                    <>
                      <Upload className="w-8 h-8 text-[#DF631E] mx-auto mb-3" />
                      <p className="text-sm text-[#4E2118]/70 mb-2">
                        {t('careers.form.cvDropzone')}
                      </p>
                      <p className="text-xs text-[#4E2118]/50 mb-4">
                        {t('careers.form.cvHint')}
                      </p>
                      <input
                        ref={fileInputRef}
                        type="file"
                        id="cv"
                        name="cv"
                        accept=".pdf,.doc,.docx"
                        onChange={handleFileChange}
                        className="hidden"
                        aria-invalid={errors.cv ? 'true' : 'false'}
                        aria-describedby={errors.cv ? 'cv-error' : undefined}
                      />
                      <label
                        htmlFor="cv"
                        className="inline-flex items-center gap-2 px-4 py-2 bg-[#FDF8F3] text-[#4E2118] rounded-lg cursor-pointer hover:bg-[#F3E4D6] transition-colors text-sm font-medium"
                      >
                        <FileText className="w-4 h-4" />
                        {t('careers.form.cvButton')}
                      </label>
                    </>
                  ) : (
                    <div className="flex items-center justify-center gap-3">
                      <FileText className="w-8 h-8 text-[#DF631E]" />
                      <div className="text-left">
                        <p className="text-sm font-medium text-[#4E2118]">{cvFile.name}</p>
                        <p className="text-xs text-[#4E2118]/50">
                          {(cvFile.size / 1024 / 1024).toFixed(2)} MB
                        </p>
                      </div>
                      <button
                        type="button"
                        onClick={removeFile}
                        className="p-1 hover:bg-red-100 rounded-full transition-colors"
                        aria-label={t('careers.form.removeFile')}
                      >
                        <X className="w-5 h-5 text-red-500" />
                      </button>
                    </div>
                  )}
                </div>
                {errors.cv && (
                  <p id="cv-error" className="mt-1 text-sm text-red-600">{errors.cv}</p>
                )}
              </div>

              {/* Cover Message */}
              <div>
                <label htmlFor="message" className="block text-sm font-medium text-[#4E2118] mb-2">
                  {t('careers.form.message')} <span className="text-[#DF631E]">*</span>
                </label>
                <textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleInputChange}
                  rows={5}
                  className={`w-full px-4 py-3 rounded-lg border ${
                    errors.message ? 'border-red-300 focus:border-red-500' : 'border-[#F3E4D6] focus:border-[#DF631E]'
                  } focus:outline-none focus:ring-2 focus:ring-[#DF631E]/20 transition-colors resize-none`}
                  placeholder={t('careers.form.messagePlaceholder')}
                  aria-invalid={errors.message ? 'true' : 'false'}
                  aria-describedby={errors.message ? 'message-error' : undefined}
                />
                {errors.message && (
                  <p id="message-error" className="mt-1 text-sm text-red-600">{errors.message}</p>
                )}
              </div>

              {/* Privacy Consent */}
              <div>
                <label className="flex items-start gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    name="privacyConsent"
                    checked={formData.privacyConsent}
                    onChange={handleInputChange}
                    className="mt-1 w-5 h-5 rounded border-[#F3E4D6] text-[#DF631E] focus:ring-[#DF631E]/20 cursor-pointer"
                    aria-invalid={errors.privacyConsent ? 'true' : 'false'}
                    aria-describedby={errors.privacyConsent ? 'privacy-error' : undefined}
                  />
                  <span className="text-sm text-[#4E2118]/70">
                    {t('careers.form.privacyConsent')}{' '}
                    <a
                      href="/it/privacy-policy"
                      className="text-[#DF631E] hover:underline"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      {t('careers.form.privacyLink')}
                    </a>
                    {' '}*
                  </span>
                </label>
                {errors.privacyConsent && (
                  <p id="privacy-error" className="mt-1 text-sm text-red-600">{errors.privacyConsent}</p>
                )}
              </div>

              {/* Submit Button */}
              <div className="pt-4">
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full btn-primary py-4 text-lg disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isSubmitting ? (
                    <span className="flex items-center justify-center gap-2">
                      <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                      </svg>
                      {t('careers.form.submitting')}
                    </span>
                  ) : (
                    t('careers.form.submit')
                  )}
                </button>
              </div>
            </div>
          </form>
        </div>
      </div>
    </section>
  )
}
